import React, { useEffect, useState } from "react";
import makeStyles from "utils/makeStyles";
import withStyles from "utils/withStyles";
import Button from "@mui/material/Button";
import styles from "./styles";
import "./styles.css";
import moment from "dayjs";
import SearchBox from "../common/SearchBox";
import ContentHeader from "../common/ContentHeader";
import TableGenerator from "../../../../../Common/TableGenerator";
import CrudDialog from "../common/CrudDialog";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  handleServerErrors,
  handleMultiFilterSearch,
} from "../../utiles/helpers";
import Label from "../common/Label";
import Tooltip from "@mui/material/Tooltip";
import DeviceConnection from "../../../../../Common/TableCellComponents/DeviceConnection";
import { convertUtcToLocal, dateFormatter, currencyFormatter, getTimeZoneDifference } from "utils/helpers";
import { ExportButton } from "components/Common/Buttons";
import _ from "lodash";
import useProductSettings from "utils/useProductSettings";

const filterLabels = {
  vm_name: "inventory__planogram__cabinet_id__vm_id__vm_name",
  serial_number: "inventory__planogram__cabinet_id__vm_id__serial_number",
  operator: "inventory__planogram__cabinet_id__vm_id__company__business_name",
  location_name:
    "inventory__planogram__cabinet_id__vm_id__location__location_name",
};

const deviceTypeFields = [
  "vm_name",
  "serial_number",
  "operator",
  "location_name",
];

const useStylesBootstrap = makeStyles((theme) => ({
  arrow: {
    color: theme.palette.common.black,
  },
  tooltip: {
    backgroundColor: theme.palette.common.black,
    fontSize: 13,
  },
}));

function BootstrapTooltip(props) {
  const classes = useStylesBootstrap();
  return <Tooltip arrow placement="right" classes={classes} {...props} />;
}

const Inventory = (props) => {
  const classes = styles();
  const productSettingsData = useProductSettings();
  const metrcEnabled = productSettingsData?.enable_metrc_tag ?? false;
  const [deviceTypeList, setDeviceList] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [page, setPage] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [firstPage, setFirstPage] = useState(null);
  const [lastPage, setLastPage] = useState(null);
  const [ordering, setOrdering] = useState("-status");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modulesSelected, setModulesSelected] = useState([]);
  const [devices, setDevices] = useState([]);
  const [filterable, setFilterable] = useState(deviceTypeFields);
  const [query, setQuery] = useState("");
  const [loader, setLoader] = useState(false);
  const [searchQuery, setSearchQuery] = useState({});
  
  // State for Export Modal & Preferences
  const [exportModal, setExportModal] = useState(false);
  const [savedSelections, setSavedSelections] = useState([]);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const fields = [
    {
      key: "inventory_details",
      columnName: "Inventory details",
      label: "Inventory details",
      type: "text",
      form: false,
      visible: true,
      disableSorting: true,
      render: (_, value) => (
        <>
          <BootstrapTooltip
            title={
              !value.inventory
                ? "Inventory for the Vending Machine is not Initialised."
                : ""
            }
          >
            <Button
              size="small"
              className="m-1"
              variant="contained"
              color={value.inventory ? "primary" : "default"}
              onClick={
                value.inventory
                  ? () =>
                      navigate(`/inventory/inventory-details/${value.oro_id}`, {
                        state: {
                          serial_number: value.vm_serial,
                          vm_name: value.vm_name,
                          oro_id: value.oro_id
                        },
                      })
                  : ""
              }
            >
              View
            </Button>
          </BootstrapTooltip>
        </>
      ),
    },
    {
      key: "vm_serial",
      columnName: "VM Serial No.",
      label: "VM Serial No.",
      type: "text",
      visible: true,
    },
    {
      key: "operator_name",
      columnName: "Operator",
      label: "Operator",
      type: "text",
      visible: true,
      required: true,
      render: (value) => value ?? "---",
    },
    {
      key: "vm_name",
      columnName: "VM Name",
      label: "VM Name",
      type: "text",
      visible: true,
    },
    {
      key: "inventory_sync_status",
      columnName: "Restock Status",
      label: "Restock Status",
      type: "text",
      visible: metrcEnabled,
      disableSorting: true,
      render: (value) => {
        const map = {
          success: { text: "Success", color: "success" },
          in_progress: { text: "Update in progress", color: "warning" },
          failed: { text: "Update failed", color: "error" },
        };
        const s = map[value] || map.success;
        return <Label color={s.color}>{s.text}</Label>;
      },
    },
    {
      key: "status",
      columnName: "Network Status",
      type: "text",
      required: false,
      visible: true,
      form: false,
      render: (__, value) => (
        <DeviceConnection
          status={
            devices.find((x) => x.serial_number === value.vm_serial)?.status
          }
        />
      ),
    },
    {
      key: "location",
      columnName: "Location",
      label: "Location",
      type: "text",
      visible: true,
    },
    {
      key: "stock_level",
      columnName: "Stock level (%)",
      label: "Stock level",
      type: "text",
      visible: true,
      disableSorting: true,
      render: (_, val) => val.total_capacity === 0 ? Math.round(val.stock_level) + "%" : Math.round((val.total_stock/val.total_capacity)*100) + "%",
    },
    {
      key: "out_of_stock_racks",
      columnName: "Out of stock racks",
      label: "Out of stock racks",
      type: "text",
      visible: true,
    },
    {
      key: "total_cash",
      columnName: "Total Cash ($)",
      label: "Total Cash",
      type: "text",
      visible: true,
      render: (value) => currencyFormatter(value) ?? "---",
    },
    {
      key: "last_replenishment",
      columnName: "From the last replenishment",
      label: "From the last replenishment",
      type: "text",
      visible: true,
      render: (value) =>
        value ? dateFormatter(convertUtcToLocal(value), true) : "---",
      disableSorting: true
    },
  ];

  // Export Fields configuration for CrudDialog
  const exportFields = [

    {
      key: "selected_devices",
      label: "Select Vending Machines",
      type: "multiAutoComplete", 
      multiple: true,
      required: true,
      visible: true,
      show: true,
      selectAllValue: "ALL",
      options: [
        { label: "Select All Vending Machines", value: "ALL" },
        ...devices.map((d) => ({
          label: `${d.vm_name || 'Unknown'} (${d.serial_number})`,
          value: d.id,
        }))
      ]
    },
    {
      key: "export_type",
      label: "Select Report Type",
      type: "radio", 
      required: true,
      visible: true,
      show: true,
      options: [
        { label: "Inventory Details", value: "inventory_details" },
        { label: "Inventory Sales", value: "inventory_sales" }
      ]
    }
  ];

  const setup = () => {
    setLoader(true);
    setDeviceList([]);
    setModulesSelected([]);
  };

  const handleRes = (data) => {
    setDeviceList(data?.results);
    setNextPage(data.next);
    setPreviousPage(data.previous);
    setFirstPage(data.first);
    setLastPage(data.last);
    setLoader(false);
    setDataCount(data.count);
  };

  const getDeviceType = async (order, max, customPage = page, call_device=false) => {
    if(call_device){
      try {
        setLoader(true);
        const { data: device } = await window.axiosIns("device", {
          params: { all: true, ordering: "vm_name" },
        });
        setDevices(device?.data?.results || []);
      } catch (err) {}
    }

    try {
      const params = {
        ...searchQuery,
        limit: max ? max : rowsPerPage,
        ordering: order ? order : ordering,
        page: customPage + 1,
        peer_device: true,
      };
      setup();
      const { data: inventoryData } = await window.axiosIns(
        "inventory_analytics",
        { params }
      );
      handleRes(inventoryData);
    } catch (err) {
      setLoader(false);
    }
  };

useEffect(() => {
    getDeviceType(0,0,0,true);

    // Fetch user's saved export preferences on component mount
    window.axiosIns.get("/inventory/export_preferences")
      .then((response) => {
        // response.data is the Axios body
        // response.data.data is your API's payload
        const fetchedData = response?.data?.data;
        
        if (fetchedData && Array.isArray(fetchedData.device_list)) {
          setSavedSelections(fetchedData.device_list);
        }
      })
      .catch((err) => console.log("Could not load export preferences", err));
  }, []);

  const handleFilter = (arr) => {
    setFilterable(arr);
    if (query !== "") {
      let searchFilter = handleMultiFilterSearch(filterLabels, arr, query);
      setup();
      setPage(0);
      window
        .axiosIns("inventory_analytics", {
          params: { ...searchFilter, limit: rowsPerPage, ordering },
        })
        .then((data = {}) => {
          handleRes(data.data);
        })
        .catch((err) => {
          setLoader(false);
        });
    }
  };

  const changePage = (url) => {
    setup();
    window.axiosIns
      .get(url)
      .then(({ data = {} }) => {
        handleRes(data);
      })
      .catch((err) => {
        setLoader(false);
        if (err.detail) {
          enqueueSnackbar(err.detail);
        } else {
          handleServerErrors(
            err,
            enqueueSnackbar,
            "Could not get device types. Try again."
          );
        }
      });
  };

  const handleSearch = (value) => {
    setQuery(value);
    let searchFilter = {};
    if (value !== "") {
      searchFilter = handleMultiFilterSearch(filterLabels, filterable, value);
    }
    setSearchQuery(searchFilter);
    setup();
    setPage(0);
    window
      .axiosIns("inventory_analytics", {
        params: { ...searchFilter, limit: rowsPerPage, ordering },
      })
      .then((data = {}) => {
        handleRes(data.data);
      })
      .catch((err) => {
        setLoader(false);
      });
  };

  const handleExportSubmit = (values) => {
      setExportModal(false);
      setLoader(true);

      let selected_ids = values.selected_devices.map(d => typeof d === 'object' ? d.value : d);
      
      setSavedSelections(selected_ids);

      const exportType = typeof values.export_type === 'object' ? values.export_type.value : values.export_type;

      const targetUrl = exportType === "inventory_sales" 
        ? "/inventory/export_inventory_sales" 
        : "/inventory/export_data"; 

      const params = {
        ...searchQuery,   
        vm_ids: selected_ids,
        state: "all",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, 
      };

      window.axiosIns.post(targetUrl, params, {
        responseType: "arraybuffer",
        headers: { "Content-Type": "application/json" },
      })
      .then((response) => {
        setLoader(false);
        enqueueSnackbar("Successfully exported");
        
        const dateStr = moment().format("MM-DD-YYYY");
        const fileName = exportType === "inventory_sales" 
          ? `Inventory-Sales-Summary-${dateStr}.xlsx`
          : `Inventory-Details-Summary-${dateStr}.xlsx`;

        const blob = new Blob([response.data], { 
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        setLoader(false);
        console.error("Export Error:", error);
        enqueueSnackbar("Failed to export data. Please try again.", { variant: "error" });
      });
    };
    
  return (
    <div id="sa-modules-wrapper" className={classes.wrapper}>
      <ContentHeader
        description="All Inventory that are for sale automatically show up here. You can edit these Inventory to and filter that Inventory."
      />
      <div className={classes.toolbar}>
        <div className={classes.crudButtons}>
          <ExportButton
            label="Export"
            onClick={() => setExportModal(true)} 
          />
        </div>
        <div className="d-flex">
          <SearchBox
            multiple={true}
            query={query}
            onChange={handleFilter}
            fields={deviceTypeFields}
            selectedFields={filterable}
            handleSearch={handleSearch}
          />
        </div>
      </div>
      <div className={classes.content}>
        <TableGenerator
          sensorTable={true}
          searchQuery={query}
          initialSort={"id"}
          searchColumnsFilter={true}
          fields={fields}
          loader={loader}
          data={deviceTypeList}
          currentPage={page}
          handleSortChange={(ordering) => {
            setOrdering(ordering);
            getDeviceType(ordering);
          }}
          onPageChange={(page, direction) => {
            setPage(page);
            if (direction === "next") {
              changePage(nextPage);
            } else if (direction === "back") {
              changePage(previousPage);
            } else if (direction === "first") {
              changePage(firstPage);
            } else if (direction === "last") {
              changePage(lastPage);
            }
          }}
          backendPagination={true}
          disablePagination={false}
          onRowPerPageChange={(rows) => {
            getDeviceType(null, rows, 0);
            setRowsPerPage(rows);
            setPage(0);
          }}
          dataCount={dataCount}
          selectedRecords={modulesSelected}
          rowOnePage={10}
          onChangeSelected={(modulesSelected) => {
            setModulesSelected(modulesSelected)
          }}
        />

        <CrudDialog
          title="Export Inventory Data"
          okText="Download"
          description="Select your desired report type and the Vending Machines you wish to include."
          fields={exportFields}
          values={{ 
            export_type: "inventory_details", 
            selected_devices: savedSelections // Defaults to [] when empty
          }} 
          onSubmit={handleExportSubmit}
          open={exportModal}
          onClose={() => setExportModal(false)}
        />
      </div>
    </div>
  );
};

export default withStyles({}, { withTheme: true })(Inventory);